import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tab", "content", "scroll", "fadeLeft", "fadeRight"]

  connect() {
    this.updateFades = this.updateFades.bind(this)
    if (this.hasScrollTarget) {
      this.scrollTarget.addEventListener("scroll", this.updateFades, { passive: true })
      window.addEventListener("resize", this.updateFades)
      // Run after layout so scrollWidth/clientWidth are settled.
      requestAnimationFrame(this.updateFades)
    }
  }

  disconnect() {
    if (this.hasScrollTarget) {
      this.scrollTarget.removeEventListener("scroll", this.updateFades)
      window.removeEventListener("resize", this.updateFades)
    }
  }

  // Show the left/right scroll hints only when the tab strip actually overflows
  // and there is more to reveal in that direction.
  updateFades() {
    if (!this.hasScrollTarget) return

    const el = this.scrollTarget
    const overflowing = el.scrollWidth > el.clientWidth + 1
    const atStart = el.scrollLeft <= 1
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1

    if (this.hasFadeLeftTarget) this.setHintHidden(this.fadeLeftTarget, !overflowing || atStart)
    if (this.hasFadeRightTarget) this.setHintHidden(this.fadeRightTarget, !overflowing || atEnd)
  }

  // The hints sit beside the strip rather than on top of it, so a hidden one
  // must give its width back — `hidden` as well as opacity, or the tabs stay
  // squeezed by two invisible buttons. pointer-events tracks visibility too.
  setHintHidden(target, hidden) {
    target.classList.toggle("opacity-0", hidden)
    target.classList.toggle("pointer-events-none", hidden)
    target.classList.toggle("hidden", hidden)
    target.classList.toggle("flex", !hidden)
  }

  scrollLeft(event) {
    if (event) event.preventDefault()
    this.scrollByStep(-1)
  }

  scrollRight(event) {
    if (event) event.preventDefault()
    this.scrollByStep(1)
  }

  // Scroll the tab strip by ~75% of its visible width (min 120px) so each click
  // reveals the next set of tabs. The scroll event re-runs updateFades.
  scrollByStep(direction) {
    if (!this.hasScrollTarget) return

    const el = this.scrollTarget
    const step = Math.max(120, Math.round(el.clientWidth * 0.75))
    el.scrollBy({ left: direction * step, behavior: "smooth" })
  }

  switch(event) {
    event.preventDefault()

    const clickedTab = event.currentTarget
    const tabName = clickedTab.dataset.tabName

    this.tabTargets.forEach(tab => {
      tab.classList.remove("ps-tab-active")
      tab.classList.add("ps-tab-inactive")
    })
    clickedTab.classList.remove("ps-tab-inactive")
    clickedTab.classList.add("ps-tab-active")

    // Hide all content sections
    this.contentTargets.forEach(content => {
      content.classList.add("hidden")
      content.classList.remove("block")
    })

    // Show the corresponding content
    const activeContent = this.contentTargets.find(
      content => content.dataset.contentName === tabName
    )

    if (activeContent) {
      activeContent.classList.remove("hidden")
      activeContent.classList.add("block")
    }
  }
}
